import {useState} from 'react'
import {useMutation} from '@tanstack/react-query'
import {ApiError} from '@/api/client'
import type {DefinitionChangeImpactResponse} from '@/types/attribute-definition'
import type {UseMutationOptions, UseMutationResult} from '@tanstack/react-query'

/**
 * Variables type that every confirmed mutation uses.
 * The base variables (TBase) are extended with the confirmed/migrationSql fields
 * that the server uses to distinguish first-attempt vs. confirmed re-send.
 */
export type ConfirmedVariables<TBase> = TBase & {
    confirmed?: boolean
    migrationSql?: string
}

export interface UseConfirmedMutationResult<TData, TVariables> {
    mutation: UseMutationResult<TData, ApiError, ConfirmedVariables<TVariables>>
    impact: DefinitionChangeImpactResponse | null
    impactDialogOpen: boolean
    confirmAndResend: (migrationSql?: string) => void
    cancelImpactDialog: () => void
    closeImpactDialog: () => void
}

/**
 * Generic hook that implements the server-steered confirmed-mutation pattern:
 *
 *  1. Caller fires mutation with variables (confirmed defaults to false)
 *  2. On 409 with DefinitionChangeImpactResponse body → opens impact dialog
 *  3. On confirm → resends same variables with confirmed: true (+optional migrationSql)
 *
 * The hook is not attribute-definition-specific; any mutation that follows the
 * confirmed/409 protocol can use it.
 */
export function useConfirmedMutation<TData, TVariables>(
    options: UseMutationOptions<TData, ApiError, ConfirmedVariables<TVariables>>,
): UseConfirmedMutationResult<TData, TVariables> {
    const [impact, setImpact] = useState<DefinitionChangeImpactResponse | null>(null)
    const [impactDialogOpen, setImpactDialogOpen] = useState(false)
    const [pendingVariables, setPendingVariables] = useState<ConfirmedVariables<TVariables> | null>(null)

    const mutation = useMutation<TData, ApiError, ConfirmedVariables<TVariables>>({
        ...options,
        onSuccess: (data, variables, onMutateResult, context) => {
            setImpact(null)
            setImpactDialogOpen(false)
            setPendingVariables(null)
            options.onSuccess?.(data, variables, onMutateResult, context)
        },
        onError: (error, variables, onMutateResult, context) => {
            if (error.status === 409) {
                const impactBody = tryParseImpactBody(error)
                if (impactBody !== null) {
                    setImpact(impactBody)
                    setImpactDialogOpen(true)
                    setPendingVariables(variables)
                    return
                }
            }
            options.onError?.(error, variables, onMutateResult, context)
        },
    })

    function confirmAndResend(migrationSql?: string) {
        if (!pendingVariables) return
        const confirmedVars: ConfirmedVariables<TVariables> = {
            ...pendingVariables,
            confirmed: true,
            ...(migrationSql ? {migrationSql} : {}),
        }
        setImpactDialogOpen(false)
        mutation.mutate(confirmedVars)
    }

    function cancelImpactDialog() {
        setImpact(null)
        setImpactDialogOpen(false)
        setPendingVariables(null)
    }

    function closeImpactDialog() {
        setImpactDialogOpen(false)
    }

    return {mutation, impact, impactDialogOpen, confirmAndResend, cancelImpactDialog, closeImpactDialog}
}

function tryParseImpactBody(error: ApiError): DefinitionChangeImpactResponse | null {
    if (!error.errorDetail) return null
    try {
        const parsed = JSON.parse(error.errorDetail) as Partial<DefinitionChangeImpactResponse>
        if (parsed.migrationRequired !== undefined) {
            return parsed as DefinitionChangeImpactResponse
        }
    } catch (e) {
        void e // not a JSON impact body — errorDetail is not parseable, treat as non-impact 409
    }
    return null
}
